<template>
  <div class="patient-item">
    <div class="patient-avatar">
      <img v-if="assignee.avatar" :src="assignee.avatar" :alt="assignee.name" />
      <div v-else class="avatar-placeholder">{{ assignee.initials }}</div>
    </div>
    
    <div class="patient-info">
      <!-- Patient Name + Actions Row -->
      <div class="patient-header">
        <div class="flex flex-col">
          <div class="patient-name">{{ assignee.name }}</div>
          <div class="patient-meta">
            <span class="patient-id">Bio Gender: {{ assignee.bioGender || 'N/A' }}</span>
            <span class="patient-id">DOB: {{ assignee.dob || 'N/A' }}</span>
            <span class="patient-added">Joined on {{ assignee.joinedDate }}</span>
          </div>
        </div>
        <div class="patient-actions-group">
          <!-- Pause/Resume Button - Hide when completed -->
          <button 
            v-if="!isCompleted"
            class="action-button pause-button" 
            :class="{ paused: assignee.isPaused }"
            @click="$emit('toggle-pause', assignee.id)"
          >
            <svg v-if="assignee.isPaused" class="action-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 0C4.03125 0 0 4.03125 0 9C0 13.9688 4.03125 18 9 18C13.9688 18 18 13.9688 18 9C18 4.03125 13.9688 0 9 0ZM9 16.3125C4.96875 16.3125 1.6875 13.0312 1.6875 9C1.6875 4.96875 4.96875 1.6875 9 1.6875C13.0312 1.6875 16.3125 4.96875 16.3125 9C16.3125 13.0312 13.0312 16.3125 9 16.3125ZM6.75 5.0625V12.9375L13.5 9L6.75 5.0625Z" fill="#004879"/>
            </svg>
            <svg v-else class="action-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 0C4.03125 0 0 4.03125 0 9C0 13.9688 4.03125 18 9 18C13.9688 18 18 13.9688 18 9C18 4.03125 13.9688 0 9 0ZM9 16.3125C4.96875 16.3125 1.6875 13.0312 1.6875 9C1.6875 4.96875 4.96875 1.6875 9 1.6875C13.0312 1.6875 16.3125 4.96875 16.3125 9C16.3125 13.0312 13.0312 16.3125 9 16.3125ZM7.3125 5.625H8.4375V12.375H7.3125V5.625ZM9.5625 5.625H10.6875V12.375H9.5625V5.625Z" fill="#004879"/>
            </svg>
            <span class="action-text">{{ assignee.isPaused ? 'Resume Usage' : 'Pause Usage' }}</span>
          </button>
          <!-- Delete Button -->
          <button class="action-button delete-button" @click="$emit('remove', assignee.id)">
            <svg class="action-icon" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path d="M13 1.30833L11.6917 0L6.5 5.19167L1.30833 0L0 1.30833L5.19167 6.5L0 11.6917L1.30833 13L6.5 7.80833L11.6917 13L13 11.6917L7.80833 6.5L13 1.30833Z" fill="#D10000"/>
            </svg>
            <span class="action-text">Remove Patient</span>
          </button>
        </div>
      </div>
      <!-- Two Progress Bars Side by Side -->
      <div class="progress-section">
        <!-- Progress Bar 1 - Program Steps -->
        <div class="progress-item">
          <p class="progress-label">{{ stepsCompleted }}/{{ stepsTotal }} steps completed ({{ stepsPercentage }}%)</p>
          <div class="progress-bar-container">
            <div class="progress-bar" :style="{ width: stepsPercentage + '%' }"></div>
          </div>
        </div>
        <!-- Progress Bar 2 - Date-based Progress -->
        <div class="progress-item">
          <p class="progress-label">{{ daysRemaining }}/{{ daysTotal }} days remaining ({{ daysPercentage }}%)</p>
          <div class="progress-bar-container">
            <div class="progress-bar" :style="{ width: daysPercentage + '%' }"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProgramAssigneeItem',
  props: {
    assignee: {
      type: Object,
      required: true
    },
    programStepsTotal: {
      type: Number,
      default: 0
    }
  },
  emits: ['toggle-pause', 'remove'],
  computed: {
    isCompleted() {
      return this.assignee.status === 'completed';
    },
    
    // Calculate steps completed from raw API data
    stepsCompleted() {
      if (this.isCompleted) {
        return this.programStepsTotal;
      }
      return this.assignee.program_steps_executed || this.assignee.stepsCompleted || 0;
    },
    
    stepsTotal() {
      return this.programStepsTotal || this.assignee.stepsTotal || 0;
    },
    
    stepsPercentage() {
      if (!this.stepsTotal) return 0;
      return Math.round((this.stepsCompleted / this.stepsTotal) * 100);
    },
    
    // Calculate date-based progress from raw API data
    dateProgress() {
      const startDate = this.assignee.program_start_date || this.assignee.programStartDate;
      const endDate = this.assignee.program_end_date || this.assignee.programEndDate;
      
      if (!startDate || !endDate) {
        return { totalDays: 0, remainingDays: 0, elapsedDays: 0 };
      }
      
      const start = new Date(startDate);
      const end = new Date(endDate);
      const now = new Date();
      
      const totalMs = end.getTime() - start.getTime();
      const totalDays = Math.ceil(totalMs / (1000 * 60 * 60 * 24));
      
      // Before start date
      if (now < start) {
        return { totalDays, remainingDays: totalDays, elapsedDays: 0 };
      }
      
      // After end date
      if (now > end) {
        return { totalDays, remainingDays: 0, elapsedDays: totalDays };
      }
      
      // During program
      const elapsedMs = now.getTime() - start.getTime();
      const elapsedDays = Math.floor(elapsedMs / (1000 * 60 * 60 * 24));
      
      const remainingMs = end.getTime() - now.getTime();
      const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
      
      return { totalDays, remainingDays, elapsedDays };
    },
    
    daysTotal() {
      return this.dateProgress.totalDays;
    },
    
    daysRemaining() {
      return this.dateProgress.remainingDays;
    },
    
    daysElapsed() {
      return this.dateProgress.elapsedDays;
    },
    
    daysPercentage() {
      if (!this.daysTotal) return 0;
      return Math.round((this.daysElapsed / this.daysTotal) * 100);
    }
  }
};
</script>

<style scoped>
/* Patient Item */
.patient-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #FFFFFF;
  border-radius: 10px;
  margin-bottom: 12px;
  border-bottom: 1px solid #E5E7EB;
  transition: all 0.3s ease;
  width: 100%;
}

.patient-item:last-child {
  margin-bottom: 0;
}

.patient-avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.patient-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #E8E8E8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  color: #979797;
}

.patient-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.patient-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.patient-name {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333333;
  margin: 0;
  flex: 1;
  min-width: 0;
}

.patient-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666666;
}

.patient-id {
  white-space: nowrap;
}

.patient-added {
  white-space: nowrap;
}

/* Progress Section - Two bars side by side */
.progress-section {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  width: 100%;
}

.progress-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.progress-label {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #333333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background: #E0E0E0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: #004879;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.patient-actions-group {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
  flex-shrink: 0;
}

.action-icon {
  flex-shrink: 0;
  display: block;
  width: 18px;
  height: 18px;
}

.pause-button {
  padding: 4px;
}

.pause-button:hover {
  background: #E0F2FE;
  padding: 4px 8px;
  gap: 4px;
}

.pause-button:hover .action-text {
  max-width: 120px;
  opacity: 1;
  color: #004879;
  text-decoration: underline;
}

.delete-button .action-icon {
  width: 13px;
  height: 13px;
}

.action-text {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  white-space: nowrap;
  transition: all 0.2s ease;
}

/* Delete Button Hover Effect */
.delete-button:hover {
  background: #FEE2E2;
  padding: 4px 8px;
  gap: 4px;
}

.delete-button:hover .action-text {
  max-width: 120px;
  opacity: 1;
  color: #D10000;
  text-decoration: underline;
}
</style>
